import PropTypes from 'prop-types';
import { useState } from 'react';
// redux
import { useSelector } from 'react-redux';
// material
import { Box, List, ListItem, ListItemAvatar, ListItemText, Avatar, Typography, TextField, InputAdornment } from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
// components
import Iconify from '../../components/Iconify';
import GroupModal from './GroupModal';
import UserStatusIndicator from './UserStatusIndicator';
// config
import { HOST_API } from '../../config';
import { selectActiveGroupCalls } from '../../redux/slices/internalChat';
// ----------------------------------------------------------------------

InternalChatSidebar.propTypes = {
    chats: PropTypes.array.isRequired,
    activeChatId: PropTypes.string,
    onSelectChat: PropTypes.func.isRequired
};

export default function InternalChatSidebar({ chats, activeChatId, onSelectChat }) {
    const { user } = useAuth();
    const [filterName, setFilterName] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [openNewChat, setOpenNewChat] = useState(false);

    const userStatusById = useSelector((state) => state.internalChat.presence.userStatusById);
    const activeGroupCalls = useSelector(selectActiveGroupCalls);

    const unreadCount = chats.filter((chat) => chat.unreadCount > 0).length;
    const groupCount = chats.filter((chat) => chat.type === 'group').length;

    const filteredChats = chats.filter((chat) => {
        const matchesName = chat.name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1;

        if (!matchesName) return false;

        if (activeFilter === 'unread') {
            return chat.unreadCount > 0;
        }
        if (activeFilter === 'groups') {
            return chat.type === 'group';
        }
        return true;
    });

    const FilterButton = ({ label, count, value, activeFilter, setActiveFilter }) => (
        <Box
            onClick={() => setActiveFilter(value)}
            sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 3,
                cursor: 'pointer',
                bgcolor: activeFilter === value ? 'primary.lighter' : 'action.hover',
                color: activeFilter === value ? 'primary.darker' : 'text.secondary',
                typography: 'subtitle2',
                display: 'flex',
                alignItems: 'center',
                mr: 1,
                transition: 'all 0.2s',
                '&:hover': {
                    bgcolor: activeFilter === value ? 'primary.lighter' : 'action.selected',
                }
            }}
        >
            {label}
            {count > 0 && (
                <Box
                    component="span"
                    sx={{
                        ml: 0.5,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 2,
                        bgcolor: activeFilter === value ? 'primary.main' : 'text.disabled',
                        color: 'common.white',
                        fontSize: '0.75rem',
                        lineHeight: 1,
                    }}
                >
                    {count}
                </Box>
            )}
        </Box>
    );

    FilterButton.propTypes = {
        label: PropTypes.string,
        count: PropTypes.number,
        value: PropTypes.string,
        activeFilter: PropTypes.string,
        setActiveFilter: PropTypes.func,
    };

    return (
        <Box sx={{
            width: { xs: '100%', md: 320 },
            display: { xs: activeChatId ? 'none' : 'flex', md: 'flex' },
            borderRight: { xs: 'none', md: '1px solid rgba(145, 158, 171, 0.24)' },
            flexDirection: 'column'
        }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">Conversas</Typography>
                <Box sx={{ display: 'flex' }}>
                    <Iconify icon="eva:edit-2-fill" width={20} height={20} onClick={() => setOpenNewChat(true)} sx={{ cursor: 'pointer', color: 'text.secondary' }} />
                </Box>
            </Box>

            <Box sx={{ px: 2, mb: 2, display: 'flex', alignItems: 'center', overflowX: 'auto', pb: 0.5, '::-webkit-scrollbar': { display: 'none' } }}>
                <FilterButton label="Tudo" value="all" activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
                <FilterButton label="Não lidas" value="unread" count={unreadCount} activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
                <FilterButton label="Grupos" value="groups" count={groupCount} activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
            </Box>

            <GroupModal
                open={openNewChat}
                onClose={() => setOpenNewChat(false)}
                onSelectChat={onSelectChat}
            />

            <Box sx={{ px: 2, pb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder="Pesquisar..."
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" width={20} height={20} />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            <List disablePadding sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
                {filteredChats.map((chat) => (
                    <ListItem
                        button
                        key={chat._id}
                        selected={activeChatId === chat._id}
                        onClick={() => onSelectChat(chat._id)}
                        sx={{
                            py: 2,
                            px: 2.5,
                            ...(activeChatId === chat._id && { bgcolor: 'action.selected' })
                        }}
                    >
                        <ListItemAvatar>
                            {chat.type === 'private' ? (
                                <UserStatusIndicator
                                    status={(() => {
                                        const recipient = chat.participants.find(p => {
                                            const pId = p.userId._id || p.userId;
                                            return String(pId) !== String(user._id);
                                        });
                                        if (!recipient) return 'offline';
                                        const rId = recipient.userId._id || recipient.userId;
                                        return userStatusById[rId]?.online ? 'online' : 'offline';
                                    })()}
                                >
                                    <Avatar alt={chat.name} src={`${HOST_API}${chat.avatar}`} />
                                </UserStatusIndicator>
                            ) : (
                                <Avatar alt={chat.name} src={`${HOST_API}${chat.avatar}`} />
                            )}
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="subtitle2" noWrap>{chat.name}</Typography>
                                    {activeGroupCalls[chat._id] && (
                                        <Iconify
                                            icon={activeGroupCalls[chat._id].type === 'video' ? 'eva:video-fill' : 'eva:phone-fill'}
                                            sx={{ width: 14, height: 14, ml: 0.75, color: 'success.main' }}
                                        />
                                    )}
                                </Box>
                            }
                            primaryTypographyProps={{ noWrap: true, variant: 'subtitle2' }}
                            secondary={(() => {
                                const msg = chat.lastMessage;
                                if (!msg) return 'Nenhuma mensagem';
                                if (msg.type === 'call_log' && msg.callData) {
                                    const t = msg.callData.type === 'video' ? 'Vídeo' : 'Voz';
                                    switch (msg.callData.status) {
                                        case 'completed': return `📞 Chamada de ${t}`;
                                        case 'missed': return `📞 Chamada Perdida`;
                                        case 'rejected': return `📞 Chamada Recusada`;
                                        case 'busy': return `📞 Linha Ocupada`;
                                        default: return `📞 Chamada de ${t}`;
                                    }
                                }
                                if (msg.content) return msg.content;
                                if (msg.attachments?.length > 0) return '📎 Anexo';
                                return '';
                            })()}
                            secondaryTypographyProps={{ noWrap: true, variant: 'body2', color: 'text.secondary' }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                {/* Format time */}
                                {new Date(chat.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            {chat.unreadCount > 0 && (
                                <Box
                                    sx={{
                                        bgcolor: 'error.main',
                                        color: 'common.white',
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        typography: 'caption',
                                        mt: 0.5
                                    }}
                                >
                                    {chat.unreadCount}
                                </Box>
                            )}
                        </Box>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
}
